import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
	digitsOnly,
	needsBrazilianAreaCode,
	extractBrazilianLocalSubscriber,
	composeBrazilianNumber
} from "../../utils/brazilianPhone";
import { Grid } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},

    textCenter: {
        backgroundColor: 'red'
    }
}));


const splitNumberFields = (rawNumber = "") => {
	const d = digitsOnly(rawNumber);
	if (d.startsWith("55") && (d.length === 12 || d.length === 13)) {
		return { ddd: d.slice(2, 4), number: d.slice(4) };
	}
	if (!d.startsWith("55") && (d.length === 10 || d.length === 11)) {
		return { ddd: d.slice(0, 2), number: d.slice(2) };
	}
	if (needsBrazilianAreaCode(rawNumber)) {
		return { ddd: "", number: extractBrazilianLocalSubscriber(rawNumber) };
	}
	return { ddd: "", number: rawNumber || "" };
};

const applyNumberBlurSplit = (value, setFieldValue) => {
	const d = digitsOnly(value || "");
	if (
		(d.startsWith("55") && (d.length === 12 || d.length === 13)) ||
		(!d.startsWith("55") && (d.length === 10 || d.length === 11))
	) {
		const split = splitNumberFields(value);
		setFieldValue("ddd", split.ddd);
		setFieldValue("number", split.number);
	}
};

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	ddd: Yup.string()
		.matches(/^[1-9][0-9]$/, "DDD inválido")
		.required("Informe o DDD"),
	number: Yup.string()
		.matches(/^\d{8,9}$/, "Número local: 8 ou 9 dígitos")
		.required("Required"),
	email: Yup.string().email("Invalid email"),
});

export function ContactForm ({ initialContact, onSave, onCancel }) {
	const classes = useStyles();

	const normalizeContact = (raw) => {
		const split = splitNumberFields(raw?.number);
		return {
			...raw,
			ddd: split.ddd,
			number: split.number || raw?.number || ""
		};
	};

	const [contact, setContact] = useState(() => normalizeContact(initialContact));

    useEffect(() => {
        setContact(normalizeContact(initialContact));
    }, [initialContact]);

	const handleSaveContact = async values => {
		try {
			const localNumber = values.number;
			const payload = {
				...values,
				number: composeBrazilianNumber(values.ddd, localNumber),
				ddd: values.ddd,
				localNumber
			};
			if (contact.id) {
				await api.put(`/contacts/${contact.id}`, payload);
			} else {
				const { data } = await api.post("/contacts", payload);
				if (onSave) {
					onSave(data);
				}
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

    return (
        <Formik
            initialValues={contact}
            enableReinitialize={true}
            validationSchema={ContactSchema}
            onSubmit={(values, actions) => {
                setTimeout(() => {
                    handleSaveContact(values);
                    actions.setSubmitting(false);
                }, 400);
            }}
        >
            {({ values, errors, touched, isSubmitting, setFieldValue, setFieldTouched }) => (
                <Form>
                    <Grid container spacing={1}>
                        {/* <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                {i18n.t("contactModal.form.mainInfo")}
                            </Typography>
                        </Grid> */}
                        <Grid item xs={12}>
                            <Field
                                as={TextField}
                                label={i18n.t("contactModal.form.name")}
                                name="name"
                                autoFocus
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                variant="outlined"
                                margin="dense"
                                className={classes.textField}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Field
                                as={TextField}
                                label="DDD"
                                name="ddd"
                                error={touched.ddd && Boolean(errors.ddd)}
                                helperText={touched.ddd && errors.ddd || "Código de área obrigatório (ex.: 11)"}
                                placeholder="11"
                                inputProps={{ maxLength: 2 }}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <Field
                                as={TextField}
                                label={i18n.t("contactModal.form.number")}
                                name="number"
                                error={touched.number && Boolean(errors.number)}
                                helperText={
                                    touched.number && errors.number
                                        || (values.ddd && values.number
                                            ? `Salvo como 55${values.ddd}${values.number}`
                                            : "Número local: 8 ou 9 dígitos")
                                }
                                placeholder="991234567"
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                onBlur={(e) => {
                                    setFieldTouched("number", true);
                                    applyNumberBlurSplit(e.target.value, setFieldValue);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Field
                                as={TextField}
                                label={i18n.t("contactModal.form.email")}
                                name="email"
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                                placeholder="Email address"
                                fullWidth
                                margin="dense"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} spacing={1}>
                            <Grid container spacing={1}>
                                <Grid xs={6} item>
                                    <Button
                                        onClick={onCancel}
                                        color="secondary"
                                        disabled={isSubmitting}
                                        variant="outlined"
                                        fullWidth
                                    >
                                        {i18n.t("contactModal.buttons.cancel")}
                                    </Button>
                                </Grid>
                                <Grid classes={classes.textCenter} xs={6} item>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        disabled={isSubmitting}
                                        variant="contained"
                                        className={classes.btnWrapper}
                                        fullWidth
                                    >
                                        {contact.id
                                            ? `${i18n.t("contactModal.buttons.okEdit")}`
                                            : `${i18n.t("contactModal.buttons.okAdd")}`}
                                        {isSubmitting && (
                                            <CircularProgress
                                                size={24}
                                                className={classes.buttonProgress}
                                            />
                                        )}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    )
}
